import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { signUpWithPassword } from '@/lib/api/auth';
import { CITIES, COUNTRIES, INDUSTRIES } from '@/lib/constants';
import { cn } from '@/lib/utils/cn';

// ── Step 1 schema ──────────────────────────────────────────────────────────
const step1Schema = z.object({
  display_name: z.string().min(1, 'Required').max(80),
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'At least 8 characters'),
  current_city: z.string().max(80).optional(),
  current_country: z.string().max(80).optional(),
});
type Step1Values = z.infer<typeof step1Schema>;

// ── Step 2 schema ──────────────────────────────────────────────────────────
const step2Schema = z.object({
  industry: z.string().optional(),
  role_title: z.string().max(100).optional(),
  company: z.string().max(100).optional(),
  linkedin_url: z.union([z.literal(''), z.string().url('Enter a valid URL')]),
});
type Step2Values = z.infer<typeof step2Schema>;

function StepIndicator({ step }: { step: 1 | 2 }) {
  return (
    <div className="mb-6 flex items-center gap-3" aria-label={`Step ${step} of 2`}>
      {([1, 2] as const).map((n) => (
        <div key={n} className="flex items-center gap-2">
          <span
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
              step >= n
                ? 'bg-gold text-navy'
                : 'bg-navy/10 text-muted',
            )}
            aria-current={step === n ? 'step' : undefined}
          >
            {n}
          </span>
          <span className={cn('text-xs', step >= n ? 'text-navy' : 'text-muted')}>
            {n === 1 ? 'Your info' : 'Professional'}
          </span>
          {n === 1 && (
            <span className="text-border" aria-hidden="true">→</span>
          )}
        </div>
      ))}
    </div>
  );
}

export function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<1 | 2>(1);
  const [step1Data, setStep1Data] = useState<Step1Values | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form1 = useForm<Step1Values>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      display_name: '',
      email: '',
      password: '',
      current_city: '',
      current_country: '',
    },
  });

  const form2 = useForm<Step2Values>({
    resolver: zodResolver(step2Schema),
    defaultValues: { industry: '', role_title: '', company: '', linkedin_url: '' },
  });

  const onStep1 = (values: Step1Values) => {
    setStep1Data(values);
    setStep(2);
  };

  const onStep2 = async (values: Step2Values) => {
    if (!step1Data) return;
    setSubmitError(null);
    try {
      const data = await signUpWithPassword(
        step1Data.email,
        step1Data.password,
        {
          display_name: step1Data.display_name,
          current_city: step1Data.current_city || undefined,
          current_country: step1Data.current_country || undefined,
          industry: values.industry || undefined,
          role_title: values.role_title || undefined,
          company: values.company || undefined,
          linkedin_url: values.linkedin_url || undefined,
        },
      );
      if (data.session) {
        // Email confirmation off — signed in immediately; AuthInit creates profile.
        // Forward any `from` location state (e.g. a claim page) so Me.tsx can
        // redirect back there once the new profile is ready.
        navigate('/me', { replace: true, state: location.state });
      } else {
        navigate('/signup/check-email', {
          replace: true,
          state: { email: step1Data.email },
        });
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Sign up failed.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="mb-8 flex items-center justify-center gap-3 text-navy"
        >
          <img src="/brand/cpn_mark.svg" alt="" aria-hidden="true" className="h-10 w-10" />
          <span className="font-display text-base font-semibold">
            The Cambodian Professionals Network
          </span>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle>Create your account</CardTitle>
            <CardDescription>
              Join the network to access the full member map and directory.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StepIndicator step={step} />

            {step === 1 && (
              <form
                onSubmit={form1.handleSubmit(onStep1)}
                noValidate
                className="flex flex-col gap-4"
              >
                <FormField
                  id="display_name"
                  label="Full name"
                  required
                  error={form1.formState.errors.display_name?.message}
                >
                  <Input
                    id="display_name"
                    autoComplete="name"
                    aria-invalid={!!form1.formState.errors.display_name}
                    {...form1.register('display_name')}
                  />
                </FormField>
                <FormField
                  id="email"
                  label="Email"
                  required
                  error={form1.formState.errors.email?.message}
                >
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={!!form1.formState.errors.email}
                    {...form1.register('email')}
                  />
                </FormField>
                <FormField
                  id="password"
                  label="Password"
                  required
                  hint="At least 8 characters."
                  error={form1.formState.errors.password?.message}
                >
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    aria-invalid={!!form1.formState.errors.password}
                    {...form1.register('password')}
                  />
                </FormField>
                <FormField
                  id="current_city"
                  label="Current city"
                  optional
                  hint="Helps find nearby members."
                  error={form1.formState.errors.current_city?.message}
                >
                  <SelectWithOther
                    id="current_city"
                    control={form1.control}
                    name="current_city"
                    options={CITIES}
                    placeholder="Select your city"
                    otherPlaceholder="e.g. Pailin"
                  />
                </FormField>
                <FormField
                  id="current_country"
                  label="Current country"
                  optional
                  error={form1.formState.errors.current_country?.message}
                >
                  <SelectWithOther
                    id="current_country"
                    control={form1.control}
                    name="current_country"
                    options={COUNTRIES}
                    placeholder="Select your country"
                    otherPlaceholder="e.g. Indonesia"
                  />
                </FormField>
                <Button type="submit" variant="gold" className="mt-1">
                  Continue →
                </Button>
              </form>
            )}

            {step === 2 && (
              <form
                onSubmit={form2.handleSubmit(onStep2)}
                noValidate
                className="flex flex-col gap-4"
              >
                <FormField
                  id="industry"
                  label="Industry"
                  optional
                  error={form2.formState.errors.industry?.message}
                >
                  <SelectWithOther
                    id="industry"
                    control={form2.control}
                    name="industry"
                    options={INDUSTRIES}
                    placeholder="Select industry"
                    otherPlaceholder="e.g. Aviation"
                  />
                </FormField>
                <FormField
                  id="role_title"
                  label="Role / Title"
                  optional
                  error={form2.formState.errors.role_title?.message}
                >
                  <Input
                    id="role_title"
                    placeholder="e.g. Software Engineer"
                    {...form2.register('role_title')}
                  />
                </FormField>
                <FormField
                  id="company"
                  label="Company"
                  optional
                  error={form2.formState.errors.company?.message}
                >
                  <Input id="company" {...form2.register('company')} />
                </FormField>
                <FormField
                  id="linkedin_url"
                  label="LinkedIn URL"
                  optional
                  hint="Boosts verification tier."
                  error={form2.formState.errors.linkedin_url?.message}
                >
                  <Input
                    id="linkedin_url"
                    type="url"
                    placeholder="https://linkedin.com/in/…"
                    aria-invalid={!!form2.formState.errors.linkedin_url}
                    {...form2.register('linkedin_url')}
                  />
                </FormField>

                {submitError && (
                  <p
                    role="alert"
                    aria-live="polite"
                    className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive"
                  >
                    {submitError}
                  </p>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost-dark"
                    onClick={() => setStep(1)}
                  >
                    ← Back
                  </Button>
                  <Button
                    type="submit"
                    variant="gold"
                    className="flex-1"
                    disabled={form2.formState.isSubmitting}
                  >
                    {form2.formState.isSubmitting
                      ? 'Creating account…'
                      : 'Create account'}
                  </Button>
                </div>
                <p className="text-center text-xs text-muted">
                  These fields are optional — you can update them later from your profile.
                </p>
              </form>
            )}

            <p className="mt-6 text-center text-sm text-muted">
              Already a member?{' '}
              <Link to="/signin" className="font-medium text-gold-dark hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FormField({
  id,
  label,
  required,
  optional,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {label}
        {required && <span className="ml-1 text-destructive" aria-hidden="true">*</span>}
        {optional && <span className="ml-1 text-xs font-normal text-muted">(Optional)</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-muted">{hint}</p>}
      {error && (
        <p role="alert" aria-live="polite" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

// Native <select> plus an inline Input that appears when the user picks
// "Other (specify)". Kept consistent with the existing native-select styling
// used elsewhere in this file — see CLAUDE.md TODO to migrate all form
// dropdowns to a shadcn Select primitive in a later pass.
const OTHER_SENTINEL = '__other__';

function SelectWithOther<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  id,
  control,
  name,
  options,
  placeholder = 'Select…',
  otherPlaceholder,
}: {
  id: string;
  control: Control<TFieldValues>;
  name: TName;
  options: readonly string[];
  placeholder?: string;
  otherPlaceholder?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const raw = (field.value as string | undefined) ?? '';
        const isKnown = raw !== '' && options.includes(raw);
        const isOther = raw !== '' && !isKnown;
        const selectValue = isKnown ? raw : (isOther ? OTHER_SENTINEL : '');
        return (
          <>
            <select
              id={id}
              value={selectValue}
              onBlur={field.onBlur}
              onChange={(e) => {
                const v = e.target.value;
                field.onChange(v === OTHER_SENTINEL ? '' : v);
              }}
              className={cn(
                'flex h-11 w-full rounded-lg border border-border bg-white px-4 py-2 text-sm text-ink shadow-sm',
                'focus-visible:border-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30',
              )}
            >
              <option value="">{placeholder}</option>
              {options.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
              <option value={OTHER_SENTINEL}>Other (specify)</option>
            </select>
            {(isOther || selectValue === OTHER_SENTINEL) && (
              <Input
                className="mt-2"
                placeholder={otherPlaceholder}
                value={isKnown ? '' : raw}
                onChange={(e) => field.onChange(e.target.value)}
                aria-label={`${placeholder} (custom)`}
              />
            )}
          </>
        );
      }}
    />
  );
}
